import trim from 'lodash/trim';
import isEmail from 'validator/lib/isEmail';
import isEmpty from 'validator/lib/isEmpty';
import isLength from 'validator/lib/isLength';

import type { CompanySize } from 'src/data/company';
import type { Entity, ID } from 'src/lib/collection';

export interface User extends Entity {
  email: string;
  name?: string;
  customers?: CustomerUserRole[];
  companyInformation?: CompanyInformation;
  country?: string;
  isKeywee?: boolean;
  isConfirmed?: boolean;
  settings?: UserSettings;
  createdAt: string;
}

interface CustomerUserRole extends Entity {
  name: string;
  role: UserRole;
  isInvited?: boolean;
}

interface CompanyInformation {
  company_size?: string;
  user_roles?: UserRole[];
}

interface ValidationResult {
  valid: boolean;
  messages: string[];
}

export interface UserSettings {
  extensionCustomerId?: ID;
  extensionWorkspaceId?: ID;
  copilotCustomerId?: ID;
  copilotWorkspaceId?: ID;
}

const MAX_NAME_LENGTH = 60;

export const SOCIAL_AUTH_STATE = {
  authorized: 'authorized',
  unauthorized: 'unauthorized',
  tokenIrrelevant: 'tokenIrrelevant',
} as const;
export type SocialAuthState = (typeof SOCIAL_AUTH_STATE)[keyof typeof SOCIAL_AUTH_STATE];

export const validateEmail = (email: string): ValidationResult => {
  const messages = [];

  if (isEmpty(trim(email))) {
    messages.push('Please enter an email address');
  } else if (!isEmail(trim(email))) {
    messages.push('Email is invalid');
  }

  return {
    valid: messages.length === 0,
    messages,
  };
};

export const validateName = (name: string): ValidationResult => {
  const messages = [];

  if (name.match(/[^A-Za-z0-9&.,\-\s]/gi)) {
    messages.push('Only English characters are allowed');
  } else if (!isLength(name, { max: MAX_NAME_LENGTH })) {
    messages.push(`Names are limited to ${MAX_NAME_LENGTH} characters.`);
  }

  return {
    valid: messages.length === 0,
    messages,
  };
};

export const MARKETING_USER_POSITION = {
  other: 'Other',
  contentMarketing: 'Content Marketing',
  demandGenAcquisition: 'Demand Gen/Acquisition',
  socialMediaManager: 'Social Media Manager',
  productMarketing: 'Product Marketing',
  marketingOps: 'Marketing Ops',
  vpMarketing: 'VP Marketing',
  cmo: 'CMO',
  founderCeo: 'Founder/CEO',
} as const;
export type MarketingUserPosition =
  (typeof MARKETING_USER_POSITION)[keyof typeof MARKETING_USER_POSITION];

// Currently, almost all the "enum values" equal "visible labels"
export const getUserMarketingPositionLabel = (position: MarketingUserPosition): string => {
  return position;
};

export const USER_ROLE = {
  admin: 'admin',
  member: 'member',
} as const;
export type UserRole = (typeof USER_ROLE)[keyof typeof USER_ROLE];

const USER_ROLE_LABEL = {
  [USER_ROLE.admin]: 'Admin',
  [USER_ROLE.member]: 'Member',
};

export const getUserRoleLabel = (role: UserRole): string => {
  return USER_ROLE_LABEL[role] || role;
};

export const getUserRoleForCustomer = (user: User, customerId: ID): UserRole | undefined => {
  const userCustomer = user?.customers?.find((customer) => customer.id === customerId);
  return userCustomer?.role;
};

export const isUserInvitedForCustomer = (user: User, customerId: ID): boolean => {
  const userCustomer = user?.customers?.find((customer) => customer.id === customerId);
  return userCustomer?.isInvited ?? false;
};

export const isAdminRole = (role?: UserRole): boolean => {
  return role === USER_ROLE.admin;
};

export const getUserName = (user: User): string => {
  if (user?.name) {
    return user?.name;
  }

  if (user?.email) {
    return user?.email?.split('@')[0];
  }

  return '';
};

export const getUserCompanySize = (user: User): CompanySize | undefined =>
  user?.companyInformation?.company_size as CompanySize;

export const getUserCompanyPositions = (user: User): string[] =>
  user?.companyInformation?.user_roles || [];

export const getUserCountry = (user: User): string | undefined => user?.country;

export const isUserAdminForCustomer = (user: User, customerId: ID): boolean => {
  const roleForCustomer = getUserRoleForCustomer(user, customerId);
  return isAdminRole(roleForCustomer);
};
